/**
 * CompanyDeletion.ts
 * Handles deletion of companies from tables and data objects
 */

import type { TabId } from '../../types';
import { querySelector, querySelectorAll } from '../../ui/DOMHelpers';
import { SummaryCalculator } from './SummaryCalculator';

/**
 * Handler for company deletion across all financial tables
 */
export class CompanyDeletionHandler {
  private summaryCalculator: SummaryCalculator;

  constructor(summaryCalculator: SummaryCalculator) {
    this.summaryCalculator = summaryCalculator;
  }

  /**
   * Delete selected companies from all tables
   * Returns the names of deleted companies for data object updates
   */
  public deleteCompanies(): string[] {
    // Get all checked companies from all tabs
    const selectedCheckboxes = querySelectorAll<HTMLInputElement>(
      '.comps-table tbody input[type="checkbox"]:checked'
    );

    if (selectedCheckboxes.length === 0) {
      alert('Please select at least one company to delete.');
      return [];
    }

    // Get company names to delete
    const companyNamesToDelete = this.getCompanyNames(selectedCheckboxes);

    // Show confirmation dialog
    if (!this.showConfirmation(companyNamesToDelete.length)) {
      return [];
    }

    // Delete from all tabs
    this.removeFromTables(companyNamesToDelete);

    // Recalculate summaries
    this.recalculateSummaries();

    // Uncheck all remaining checkboxes
    this.uncheckAll();

    return companyNamesToDelete;
  }

  /**
   * Update data objects after company deletion
   */
  public updateDataObjects(
    companyNamesToDelete: string[],
    dataObjects: Record<string, unknown>
  ): void {
    // Update each data object
    Object.values(dataObjects).forEach(yearData => {
      if (typeof yearData !== 'object' || yearData === null) return;

      Object.values(yearData).forEach(dataset => {
        if (
          typeof dataset === 'object' &&
          dataset !== null &&
          'companies' in dataset &&
          Array.isArray((dataset as { companies: unknown }).companies)
        ) {
          // Type assertion to tell TypeScript about companies property
          const typedDataset = dataset as { companies: Array<{ name: string }>; summary?: Record<string, Record<string, string | number>> };

          // Filter out deleted companies
          typedDataset.companies = typedDataset.companies.filter(
            (company) => !companyNamesToDelete.includes(company.name)
          );

          // Recalculate summary statistics
          if (typedDataset.companies.length > 0) {
            this.summaryCalculator.updateDatasetSummary(typedDataset);
          }
        }
      });
    });
  }

  /**
   * Get company names from selected checkboxes
   */
  private getCompanyNames(checkboxes: HTMLInputElement[]): string[] {
    return checkboxes
      .map(checkbox => {
        const row = checkbox.closest('tr');
        if (!row) return null;

        const nameCell = row.querySelector('td:nth-child(2)');
        const text = nameCell?.textContent?.trim();
        return (text !== null && text !== undefined && text !== '') ? text : null;
      })
      .filter((name): name is string => name !== null);
  }

  /**
   * Show confirmation dialog
   */
  private showConfirmation(count: number): boolean {
    const message =
      count === 1
        ? 'Are you sure you want to delete the selected company?'
        : `Are you sure you want to delete ${count} selected companies?`;

    return confirm(message);
  }

  /**
   * Remove companies from all table tabs
   */
  private removeFromTables(companyNames: string[]): void {
    const allTabs: TabId[] = ['financial-tab', 'trading-tab', 'operating-tab'];

    allTabs.forEach(tabId => {
      const table = querySelector<HTMLTableElement>(`#${tabId} .comps-table`);
      if (!table) return;

      const rows = querySelectorAll<HTMLTableRowElement>('tbody tr', table);

      rows.forEach(row => {
        const nameCell = row.querySelector('td:nth-child(2)');
        const companyName = nameCell?.textContent?.trim();

        if (companyName !== null && companyName !== undefined && companyName !== '' && companyNames.includes(companyName)) {
          row.remove();
        }
      });
    });
  }

  /**
   * Recalculate summary statistics for all tabs
   */
  private recalculateSummaries(): void {
    const allTabs: TabId[] = ['financial-tab', 'trading-tab', 'operating-tab'];

    allTabs.forEach(tabId => {
      const table = querySelector<HTMLTableElement>(`#${tabId} .comps-table`);
      const summaryTable = querySelector<HTMLTableElement>(`#${tabId} .summary-table`);

      if (table && summaryTable) {
        this.summaryCalculator.updateTableSummary(table, summaryTable);
      }
    });
  }

  /**
   * Uncheck all checkboxes
   */
  private uncheckAll(): void {
    const checkboxes = querySelectorAll<HTMLInputElement>(
      '.comps-table tbody input[type="checkbox"]'
    );

    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });
  }

  /**
   * Get selected company names (without deleting)
   * Useful for chart generation or other operations
   */
  public getSelectedCompanies(): string[] {
    const selectedCheckboxes = querySelectorAll<HTMLInputElement>(
      '.comps-table tbody input[type="checkbox"]:checked'
    );

    return this.getCompanyNames(selectedCheckboxes);
  }
}
